import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown } from "lucide-react";
import { useLanguage } from "@/hooks/use-language";
import tiegoLogo from "@/assets/tiego-logo-white.webp";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const { language, switchPath, t, localePath } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const tgaLinks = [
    { name: "TGA – Group Accounting", href: localePath("/tga") },
    { name: t("Kundberättelser", "Customer Stories"), href: localePath("/tga/kundberattelser") },
    { name: "Export API", href: localePath("/export-api") },
    { name: t("Integrationer", "Integrations"), href: localePath("/integrationer") },
    { name: t("Implementation", "Implementation"), href: localePath("/implementation") },
  ];

  const tleLinks = [
    { name: "TLE – Lease Engine", href: localePath("/tle") },
    { name: t("Implementation", "Implementation"), href: localePath("/tle-implementation") },
    { name: t("Kommuner & regioner", "Municipalities & Regions"), href: localePath("/kommuner") },
  ];


  const kontaktLinks = [
    { name: t("Boka demo", "Book a Demo"), href: localePath("/book-a-demo") },
    { name: "Support", href: localePath("/support") },
  ];

  const DropdownMenu = ({ 
    label,
    productName,
    links, 
    id 
  }: { 
    label: string;
    productName?: string;
    links: { name: string; href: string }[]; 
    id: string;
  }) => (
    <div 
      className="relative"
      onMouseEnter={() => setOpenDropdown(id)}
      onMouseLeave={() => setOpenDropdown(null)}
    >
      <button
        className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-white/70 hover:text-white hover:bg-white/10"
      >
        {label}
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${openDropdown === id ? 'rotate-180' : ''}`} />
      </button>
      
      <div className={`absolute top-full left-0 pt-2 transition-all duration-200 ${
        openDropdown === id ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
      }`}>
        <div className="bg-navy-deep border border-white/10 rounded-xl shadow-xl min-w-[220px] py-2 backdrop-blur-xl">
          {productName && (
            <div className="px-4 py-2 border-b border-white/10 mb-1">
              <span className="text-xs font-semibold text-secondary tracking-wide uppercase">{productName}</span>
            </div>
          )}
          {links.map((link) => (
            <a
              key={link.name}
              href={link.href}
              className="block mx-2 px-3 py-2.5 text-sm text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              {link.name}
            </a>
          ))}
        </div>
      </div>
    </div>
  );

  const otherLang = language === "sv" ? "en" : "sv";
  const langLabel = language === "sv" ? "English" : "Svenska";

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? "bg-navy-deep/95 backdrop-blur-xl shadow-lg shadow-black/10" 
        : "bg-transparent"
    }`}>
      <div className="container mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href={localePath("/")} className="flex items-center group">
            <img 
              src={tiegoLogo} 
              alt="Tiego" 
              className="h-14 w-auto"
              width={176}
              height={112}
              fetchpriority="high"
              loading="eager"
              decoding="async"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-0.5">
            <DropdownMenu label={t("Koncernredovisning", "Group Accounting")} links={tgaLinks} id="tga" />
            <DropdownMenu label={t("Leasing", "Leasing")} links={tleLinks} id="tle" />
            
            <a
              href={localePath("/filmer")}
              className="px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-white/70 hover:text-white hover:bg-white/10"
            >
              {t("Filmer", "Videos")}
            </a>
            
            <a
              href={localePath("/nyheter")}
              className="px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-white/70 hover:text-white hover:bg-white/10"
            >
              {t("Nyheter", "News")}
            </a>

            <a
              href={localePath("/karriar")}
              className="px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-white/70 hover:text-white hover:bg-white/10"
            >
              {t("Karriär", "Career")}
            </a>
            <DropdownMenu label={t("Kontakta oss", "Contact Us")} links={kontaktLinks} id="kontakt" />
            
            <a
              href="https://portal.tiego.se"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-white/70 hover:text-white hover:bg-white/10"
            >
              {t("Logga in", "Log in")}
            </a>
          </nav>

          {/* CTA Button + Language */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={switchPath(otherLang)}
              hrefLang={otherLang}
              lang={otherLang}
              className="px-2 py-1 rounded text-xs font-medium transition-all duration-200 text-white/70 hover:text-white bg-white/10"
            >
              {langLabel}
            </a>
            <a href={localePath("/book-a-demo")}>
              <Button variant="hero" size="default">
                {t("Boka demo", "Book a Demo")}
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg transition-colors hover:bg-white/10"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="h-6 w-6 text-white" />
            ) : (
              <Menu className="h-6 w-6 text-white" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-6 border-t border-white/10 animate-fade-in bg-navy-deep/95 backdrop-blur-xl -mx-6 px-6 rounded-b-2xl max-h-[calc(100vh-5rem)] overflow-y-auto overscroll-contain">
            <nav className="flex flex-col gap-2">
              {/* TGA Section */}
              <div className="px-4 pt-2 pb-1">
                <span className="text-secondary text-xs font-semibold tracking-wide uppercase">TGA</span>
                <span className="text-white/50 text-xs ml-2">– {t("Koncernredovisning", "Group Accounting")}</span>
              </div>
              {tgaLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-200 text-sm font-medium py-2 px-4 rounded-xl ml-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              
              <div className="border-t border-white/10 my-2" />
              
              {/* TLE Section */}
              <div className="px-4 pt-2 pb-1">
                <span className="text-secondary text-xs font-semibold tracking-wide uppercase">TLE</span>
                <span className="text-white/50 text-xs ml-2">– {t("Leasing", "Leasing")}</span>
              </div>
              {tleLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-200 text-sm font-medium py-2 px-4 rounded-xl ml-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              
              <div className="border-t border-white/10 my-2" />
              
              <a
                href={localePath("/filmer")}
                className="text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-200 text-base font-medium py-3 px-4 rounded-xl"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("Filmer", "Videos")}
              </a>
              <a
                href={localePath("/nyheter")}
                className="text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-200 text-base font-medium py-3 px-4 rounded-xl"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("Nyheter", "News")}
              </a>
              <a
                href={localePath("/karriar")}
                className="text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-200 text-base font-medium py-3 px-4 rounded-xl"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("Karriär", "Career")}
              </a>
              <a
                href={localePath("/support")}
                className="text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-200 text-base font-medium py-3 px-4 rounded-xl"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("Kontakta oss", "Contact Us")}
              </a>
              <a
                href="https://portal.tiego.se"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-200 text-base font-medium py-3 px-4 rounded-xl"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("Logga in", "Log in")}
              </a>
              
              <div className="flex items-center gap-3 mt-4">
                <a
                  href={switchPath(otherLang)}
                  hrefLang={otherLang}
                  lang={otherLang}
                  onClick={() => setIsMenuOpen(false)}
                  className="px-3 py-2 rounded-lg text-sm text-white/70 bg-white/10"
                >
                  {langLabel}
                </a>
                <a href={localePath("/book-a-demo")} className="flex-1">
                  <Button variant="hero" size="lg" className="w-full">
                    {t("Boka demo", "Book a Demo")}
                  </Button>
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
